import { apiErrorMessage } from '@agos/api-client'
import {
  getGetPaymentsQueryKey,
  getGetSettingsPaymentsQueryKey,
  useGetPayments,
  useGetPaymentsCount,
  useGetSettingsPayments,
  usePostPaymentsPaymentIdRefund,
  usePutSettingsPayments,
} from '@agos/api-client/payments'
import { OrderNumberLink, T, formatCents, formatCount, formatDateTime, tt, useAuth } from '@agos/shared'
import {
  ConfirmDialog,
  EmptyState,
  FilterSelect,
  PageHeader,
  SearchInput,
  Spinner,
  StatCard,
  StatusBadge,
  cn,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CreditCard, Info, RefreshCw } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { mayWrite } from '../write-access'
import {
  PAGE_SIZE,
  METHODS,
  STATUSES,
  gatewayMessage,
  gatewayState,
  archiveNote,
  archiveTotalsOf,
  isTruncated,
  prefsOf,
  prefsPayload,
  refundBlock,
  refundBlockMessage,
  refundConfirmation,
  rowsOf,
  SEARCH_DEBOUNCE_MS,
  searchRows,
  methodCell,
  methodLabel,
  statusLabel,
  type PaymentRow,
} from './payment-archive'

/**
 * `/online-payments` — the card-payment archive, and two checkout rules.
 *
 * **FB-157.** `GET /payments` defaults to `limit = 50`. Legacy passes none, offers no pagination, and computes
 * four stat cards from whatever came back — so on the restored data the cards say 50 transactions and
 * **810,95 €** where the archive holds 1 355 and **26 417,25 €**, a factor of thirty-three. Choosing a status
 * filter narrows the same cards without saying so.
 *
 * **FB-158.** The search filters those fifty rows client-side while the endpoint accepts no search, so an
 * order number from last month reports *nothing found* for a payment that is in the archive.
 *
 * **And the money was a parsed float**, summed across a thousand rows. §14.7: cents.
 *
 * Ported from legacy `pages/online-payments.tsx` (489 lines).
 */

export function OnlinePaymentsPage() {
  const { user } = useAuth()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [status, setStatus] = useState('')
  // §10A.126, the owner's *"add filter for 'CardPay' and filter for 'QR Pay', so we can filter them
  // out"*. Server-side like the status and the search, so it filters the archive rather than the
  // fifty rows that happened to come back - which is FB-158's finding, one field over.
  const [method, setMethod] = useState('')
  const [search, setSearch] = useState('')
  const [confirming, setConfirming] = useState<PaymentRow | null>(null)

  const settings = useGetSettingsPayments()

  // FB-158. The search is the server's now, so an order number from last month finds its payment
  // instead of reporting nothing. Debounced the way `/settings-translations` does it: a keystroke is
  // not a query, and this one runs two of them.
  const [query, setQuery] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  const filters = {
    ...(status === '' ? {} : { status }),
    ...(method === '' ? {} : { method }),
    ...(query.trim() === '' ? {} : { search: query.trim() }),
  }
  const list = useGetPayments(Object.keys(filters).length === 0 ? undefined : filters)
  // FB-157. What the archive holds under the same filters — the cards were computed from whatever the
  // last request returned, so they said 50 transactions and 810,95 € against 1 355 and 26 417,25 €.
  const counted = useGetPaymentsCount(Object.keys(filters).length === 0 ? undefined : filters)

  const rows = useMemo(() => rowsOf(list.data), [list.data])
  const archive = useMemo(() => archiveTotalsOf(counted.data), [counted.data])
  // Local narrowing only while the debounced request is in flight, so typing stays immediate; the
  // server's answer replaces it the moment it lands.
  const visible = useMemo(
    () => (query.trim() === search.trim() ? [...rows] : searchRows(rows, search)),
    [rows, search, query],
  )
  const truncated = isTruncated(rows, archive)

  // Spread: `prefsOf` and `gatewayState` read a bag by key, and the payload is typed now
  // (BLOCKED #4). Widening here keeps those readers tolerant without a cast.
  const prefs = prefsOf({ ...settings.data })
  const gateway = gatewayState({ ...settings.data })
  const gatewayName = settings.data?.gateway ?? 'tatrapay'

  // `PUT /settings/payments` is `main_admin`; the refund is a permission, not a role.
  const mayEditPrefs = mayWrite('payments', user?.systemRole)

  const savePrefs = usePutSettingsPayments({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetSettingsPaymentsQueryKey() })
        toast({ title: tt('Nastavenie platieb uložené') })
      },
      // §9.355 - the server's own sentence, per §6, with this one as the fallback for what the
      // envelope cannot carry (a network drop, or a proxy answering HTML).
      onError: (error) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(error, tt('Na serveri sa nič nezmenilo.')),
        }),
    },
  })

  const refund = usePostPaymentsPaymentIdRefund({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetPaymentsQueryKey() })
        toast({ title: tt('Platba vrátená') })
      },
      // §9.355, FB-635 - **this sentence used to be printed for all six refusals**, five of which
      // are not the bank. The damaging one is a transfer, where the server answers with an
      // *instruction* - refund it manually from the account - and the screen replaced it with a
      // claim that the bank said no, so the operator retries a button that can never work.
      onError: (error) =>
        toast({
          variant: 'destructive',
          title: tt('Vrátenie zlyhalo'),
          description: apiErrorMessage(
            error,
            tt('Banka alebo brána ho odmietla - v archíve sa nič nezmenilo.'),
          ),
        }),
    },
  })

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Online platby')}
        description={tt('Archív kartových platieb a dve pravidlá pokladne')}
        icon={<CreditCard className="w-5 h-5" />}
        actions={
          <button
            type="button"
            data-refresh
            disabled={list.isFetching}
            onClick={() => void queryClient.invalidateQueries({ queryKey: getGetPaymentsQueryKey() })}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-card text-sm text-muted-foreground disabled:opacity-50"
          >
            <RefreshCw className={cn('w-4 h-4', list.isFetching && 'animate-spin')} /> <T>Obnoviť</T>
          </button>
        }
      />

      <p
        className={cn(
          'text-xs flex items-start gap-2',
          gateway === 'live' ? 'text-muted-foreground' : 'text-amber-500',
        )}
        data-gateway={gateway}
      >
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        {gatewayMessage(gateway, gatewayName)}
      </p>

      {/* FB-157. What the figures describe, before the figures. */}
      <p
        className="text-xs text-muted-foreground flex items-start gap-2"
        data-window={archive === null ? 'unknown' : truncated ? 'page' : 'all'}
      >
        <AlertTriangle className={cn('w-3.5 h-3.5 shrink-0 mt-0.5', truncated && 'text-amber-500')} />
        {archiveNote(rows.length, archive)}
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Counted by the server across the whole archive, not summed over the page. */}
        <StatCard
          label={tt('Transakcií')}
          value={archive === null ? '-' : formatCount(archive.total)}
          icon={CreditCard}
        />
        <StatCard
          label={statusLabel('paid')}
          value={archive === null ? '-' : formatCount(archive.paidCount)}
          color="emerald"
        />
        <StatCard
          label={statusLabel('failed')}
          value={archive === null ? '-' : formatCount(archive.failedCount)}
          color="red"
        />
        <StatCard
          label={tt('Objem zaplatených')}
          value={archive === null ? '-' : formatCents(archive.paidCents)}
          color="blue"
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <FilterSelect
          label={tt('Stav')}
          value={status}
          onChange={setStatus}
          options={[
            { value: '', label: tt('Všetky') },
            ...STATUSES.map((entry) => ({ value: entry, label: statusLabel(entry) })),
          ]}
        />
        {/* §10A.126 - the count comes off the same scan as the statuses, so the label can say how
          * many there are without a second request. */}
        <FilterSelect
          label={tt('Spôsob')}
          value={method}
          onChange={setMethod}
          options={[
            { value: '', label: tt('Všetky') },
            ...METHODS.map((entry) => ({
              value: entry,
              label:
                archive === null
                  ? methodLabel(entry)
                  : `${methodLabel(entry)} (${entry === 'card_pay' ? archive.cardPayCount : archive.qrPayCount})`,
            })),
          ]}
        />
        <div className="min-w-[220px]">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={tt('Číslo objednávky alebo referencia')}
          />
        </div>
      </div>

      {list.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<CreditCard className="w-10 h-10" />}
          title={tt('Žiadna platba')}
          description={
            search.trim() === ''
              ? tt('V tomto výbere nie je žiadna transakcia.')
              : tt('Také číslo objednávky ani referenciu archív neobsahuje.')
          }
        />
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs text-muted-foreground uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">#</th>
                  <th className="text-left px-4 py-3 font-bold">
                    <T>Č. obj.</T>
                  </th>
                  <th className="text-right px-4 py-3 font-bold">
                    <T>Suma</T>
                  </th>
                  <th className="text-left px-4 py-3 font-bold">
                    <T>Stav</T>
                  </th>
                  {/* §10A.126 - the owner asked for the two to be distinguishable, so the row says
                    * which it was rather than only the filter knowing. */}
                  <th className="text-left px-4 py-3 font-bold">
                    <T>Spôsob</T>
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    TransactionID
                  </th>
                  <th className="text-left px-4 py-3 font-bold">
                    <T>Vytvorená</T>
                  </th>
                  <th className="text-right px-4 py-3 font-bold" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visible.map((row) => {
                  const block = refundBlock(row)
                  return (
                    <tr
                      key={row.id}
                      data-payment={row.id}
                      data-status={row.status}
                      data-method={row.method ?? ''}
                      data-order={row.orderNumber ?? ''}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{row.id}</td>
                      {/* §9.143 - a payment ledger; the order it paid for is one click away. */}
                      <td className="px-4 py-3 font-mono text-xs">
                        {row.orderNumber === null ? (
                          '-'
                        ) : (
                          <OrderNumberLink
                            orderId={row.orderId}
                            className="text-primary hover:underline"
                            title={`${tt('Otvoriť v archíve')}: ${row.orderNumber}`}
                          >
                            {row.orderNumber}
                          </OrderNumberLink>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold" data-amount>
                        {formatCents(row.amountCents)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          label={statusLabel(row.status)}
                          color={
                            row.status === 'paid'
                              ? 'emerald'
                              : row.status === 'failed'
                                ? 'red'
                                : row.status === 'refunded'
                                  ? 'blue'
                                  : 'zinc'
                          }
                        />
                      </td>
                      {/* A row the gateway never named a method for reads *neuvedené* rather than
                        * blank: nothing was reported, which is a different fact from *by card*. */}
                      <td className="px-4 py-3 text-xs" data-method-cell>
                        {row.method === null ? (
                          <span className="text-muted-foreground">{methodCell(row)}</span>
                        ) : (
                          <span className="font-semibold">{methodLabel(row.method)}</span>
                        )}
                      </td>
                      {/* §9.140 - the reference the bank knows the payment by. When a customer says
                        * they were charged and the order says otherwise, this is what gets quoted;
                        * an archive that cannot produce it sends somebody to the database. A UUID is
                        * copied rather than read, so it is selectable with the whole value on hover. */}
                      <td className="px-4 py-3">
                        <span
                          data-transaction={row.id}
                          title={row.transactionId ?? ''}
                          className="block font-mono text-[10px] text-muted-foreground truncate max-w-[130px] select-all"
                        >
                          {row.transactionId ?? '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {row.createdAt === '' ? '-' : formatDateTime(row.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {block === null ? (
                          <button
                            type="button"
                            data-refund={row.id}
                            disabled={refund.isPending}
                            onClick={() => setConfirming(row)}
                            className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium disabled:opacity-50"
                          >
                            <T>Vrátiť</T>
                          </button>
                        ) : (
                          <span className="text-[11px] text-muted-foreground" data-refund-blocked={row.id}>
                            {refundBlockMessage(block)}
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {truncated && (
            <p className="px-4 py-3 text-[11px] text-muted-foreground border-t border-border" data-page-note>
              {/* No *load more*: the endpoint pages by offset and legacy had no control at all. What is
                  different is that the count is real, so this states a page of a known whole rather than
                  a page of an unknown one. */}
              <T>Zobrazených je najnovších</T> {formatCount(PAGE_SIZE)} <T>z</T>{' '}
              {formatCount(archive?.total ?? 0)} <T>transakcií.</T>
            </p>
          )}
        </div>
      )}

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            <T>Pravidlá pokladne</T>
          </h2>
        </div>
        <div className="divide-y divide-border">
          <PrefRow
            label={tt('Karta ako predvolená platba')}
            desc={tt('Pri objednávke sa najprv ponúkne platba kartou.')}
            value={prefs.defaultOnline}
            disabled={!mayEditPrefs || savePrefs.isPending}
            testId="defaultOnline"
            onToggle={() =>
              savePrefs.mutate({
                data: { ...prefsPayload({ ...prefs, defaultOnline: !prefs.defaultOnline }) },
              })
            }
          />
          <PrefRow
            label={tt('Karta aj pri vyzdvihnutí')}
            desc={tt('Zákazník môže zaplatiť kartou aj keď si objednávku vyzdvihne sám.')}
            value={prefs.allowOnlinePickup}
            disabled={!mayEditPrefs || savePrefs.isPending}
            testId="allowOnlinePickup"
            onToggle={() =>
              savePrefs.mutate({
                data: { ...prefsPayload({ ...prefs, allowOnlinePickup: !prefs.allowOnlinePickup }) },
              })
            }
          />
        </div>
        {!mayEditPrefs && (
          <p className="px-5 py-3 text-[11px] text-amber-500 border-t border-border" data-read-only>
            <T>Tieto dve pravidlá môže zmeniť len hlavný administrátor.</T>
          </p>
        )}
      </div>

      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        variant="danger"
        title={tt('Vrátiť platbu?')}
        description={confirming === null ? '' : refundConfirmation(confirming)}
        confirmLabel={tt('Vrátiť')}
        icon={<RefreshCw className="w-5 h-5" />}
        loading={refund.isPending}
        onConfirm={() => {
          const row = confirming
          setConfirming(null)
          if (row !== null) refund.mutate({ paymentId: row.id })
        }}
      />
    </div>
  )
}

function PrefRow({
  label,
  desc,
  value,
  disabled,
  testId,
  onToggle,
}: {
  label: string
  desc: string
  value: boolean
  disabled: boolean
  testId: string
  onToggle: () => void
}) {
  return (
    <div className="flex items-start justify-between px-5 py-4 gap-4" data-pref={testId}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-foreground text-sm">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        disabled={disabled}
        data-pref-state={value ? 'on' : 'off'}
        onClick={onToggle}
        className={cn(
          'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
          value ? 'bg-primary' : 'bg-muted',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
            value ? 'left-[22px]' : 'left-0.5',
          )}
        />
      </button>
    </div>
  )
}
